import { Pill } from '../../components/Common/Pill'
import { PageSection } from 'hooks/useAIChat'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'
import { useChatbotSettingsStore } from 'store/useChatbotSettingsStore'
import { classNames } from 'util/classNames'
import { PulseLoader as Loader } from 'react-spinners/'

const borderRadiusRules = (currentIndex, activity) => {
  const current = activity[currentIndex]
  const previous = activity[currentIndex - 1]
  const next = activity[currentIndex + 1]

  const isFirst = !previous || previous.type !== current.type
  const isLast = !next || next.type !== current.type
  const isMiddle = !isFirst && !isLast

  if (current.type === 'ai') {
    if (isFirst) {
      return 'rounded-xl rounded-bl-sm'
    } else if (isMiddle && !isLast) {
      return 'rounded-xl rounded-bl-sm rounded-tl-sm'
    } else if (isLast) {
      return 'rounded-xl rounded-tl-sm'
    }
  } else {
    if (isFirst) {
      return 'rounded-xl rounded-br-sm'
    } else if (isMiddle) {
      return 'rounded-xl rounded-br-sm'
    } else if (isLast) {
      return 'rounded-xl rounded-br-sm'
    }
  }
}

export const ChatMessages = ({ autoscroll = true, vectorSearch, loading }) => {
  const {
    chatbot,
    clearMessages,
    messages,
    setMessages,
    setSuggestedMessages,
  } = useChatbotSettingsStore()
  const endOfMessagesRef = useRef<HTMLDivElement>(null)
  const [showClearConversation, setShowClearConversation] = useState(false)
  const [showSuggestedMessages, setShowSuggestedMessages] = useState<boolean>(
    (chatbot?.suggested_messages?.length || 0) > 0
  )

  const handleClearMessages = () => {
    clearMessages()
    setShowClearConversation(false)
    setSuggestedMessages(chatbot?.suggested_messages || [])
    setShowSuggestedMessages(true)

    if (chatbot?.id) {
      try {
        localStorage.removeItem(`chatbot_messages_${chatbot.id}`)
      } catch (error) {
        console.error('Failed to clear messages from localStorage:', error)
      }
    }
  }

  useEffect(() => {
    const hasNonAiMessage = messages?.some(
      (message) => message.role !== 'assistant'
    )
    if (hasNonAiMessage) {
      setShowClearConversation(true)
      setShowSuggestedMessages(false)
    } else if (
      messages.length === 0 ||
      (messages.length > 0 &&
        messages.every((msg) => msg.role === 'assistant') &&
        messages.every((msg) =>
          chatbot?.starter_messages?.includes(msg.content)
        ))
    ) {
      // If there are no messages or only starter messages, show suggested messages
      setShowSuggestedMessages(true)
    }
  }, [messages, chatbot?.starter_messages])

  useEffect(() => {
    if (!autoscroll) return
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, autoscroll])

  // Update showSuggestedMessages when chatbot data changes
  useEffect(() => {
    if (chatbot?.suggested_messages && chatbot.suggested_messages.length > 0) {
      const hasOnlyStarterMessages =
        messages.length === 0 ||
        (messages.every((msg) => msg.role === 'assistant') &&
          messages.every((msg) =>
            chatbot?.starter_messages?.includes(msg.content)
          ))

      if (hasOnlyStarterMessages) {
        setShowSuggestedMessages(true)
      }
    } else {
      setShowSuggestedMessages(false)
    }
  }, [chatbot?.suggested_messages, chatbot?.starter_messages, messages])

  return (
    <ul className="flex h-full w-full flex-1 break-before-avoid flex-col justify-start overflow-y-auto bg-[#EAEEF3] p-4 pb-8">
      {messages.map((message, index) => {
        const prevMessage = messages[index - 1]

        return (
          <li key={index} className="flex w-full flex-col">
            <div
              className={classNames(
                message.role === 'assistant' ? 'justify-start' : 'justify-end',
                'relative mb-4 flex'
              )}
            >
              <div
                className={classNames(
                  'relative flex w-fit max-w-[85%] flex-col gap-y-2',
                  message.role === 'assistant' ? 'mx-auto' : ''
                )}
              >
                <div
                  className={classNames(
                    borderRadiusRules(index, messages),
                    'flex w-fit items-start justify-center p-2'
                  )}
                  style={{
                    background:
                      (message.role === 'assistant'
                        ? chatbot?.ai_background_color
                        : chatbot?.human_background_color) || '#ffffff',
                    color:
                      (message.role === 'assistant'
                        ? chatbot?.ai_text_color
                        : chatbot?.human_text_color) || '#000000',
                  }}
                >
                  <div className="text-sm leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[[gfm, { allowDangerousHtml: true }]]}
                      components={{
                        ol: ({ node, ...props }) => (
                          <ol
                            className="my-3 list-decimal space-y-2 pl-6"
                            {...props}
                          />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul
                            className="my-3 list-disc space-y-2 pl-6"
                            {...props}
                          />
                        ),
                        li: ({ node, ordered, ...props }) => (
                          <li className="my-1 pl-1" {...props} />
                        ),
                        p: ({ node, ...props }) => (
                          <p className="my-3" {...props} />
                        ),
                        h1: ({ node, ...props }) => (
                          <h1 className="my-4 text-lg font-bold" {...props} />
                        ),
                        h2: ({ node, ...props }) => (
                          <h2 className="my-3 text-base font-bold" {...props} />
                        ),
                        h3: ({ node, ...props }) => (
                          <h3 className="my-3 text-sm font-bold" {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong className="font-bold" {...props} />
                        ),
                        em: ({ node, ...props }) => (
                          <em className="italic" {...props} />
                        ),
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="my-3 border-l-4 border-gray-300 pl-4 italic"
                            {...props}
                          />
                        ),
                        hr: ({ node, ...props }) => (
                          <hr className="my-4 border-gray-300" {...props} />
                        ),
                        code: ({ node, inline, ...props }) =>
                          inline ? (
                            <code
                              className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm"
                              {...props}
                            />
                          ) : (
                            <code
                              className="block rounded bg-gray-100 p-3 font-mono text-sm"
                              {...props}
                            />
                          ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {message.sources
                    ?.filter((source) => {
                      return source.similarity > 0.5
                    })
                    .sort((a, b) => b.similarity - a.similarity)
                    .reduce((uniqueSources, source) => {
                      if (
                        !uniqueSources.some(
                          (s) => s.filename === source.filename
                        )
                      ) {
                        uniqueSources.push(source)
                      }
                      return uniqueSources
                    }, [] as PageSection[])
                    .slice(0, 3)
                    .map((source, idx) => (
                      <Pill key={idx} hasMaxWidth={false} color="gray">
                        {source.title || source.filename}
                      </Pill>
                    ))}
                </div>
              </div>
            </div>
          </li>
        )
      })}
      {loading && (
        <li className="flex w-full flex-col">
          <div className="relative mb-2 flex justify-start">
            <div className="relative flex w-fit max-w-full flex-col gap-y-2">
              <div
                className="flex w-fit items-center justify-center space-x-3 rounded-xl rounded-bl-sm p-3"
                style={{
                  background: chatbot?.ai_background_color || '#ffffff',
                  color: chatbot?.ai_text_color || '#000000',
                }}
              >
                <Loader
                  speedMultiplier={0.5}
                  size={3}
                  color={chatbot?.ai_text_color || '#000000'}
                />
              </div>
            </div>
          </div>
        </li>
      )}
      <div className="mt-auto flex max-w-full justify-end">
        {showSuggestedMessages && (
          <div className="flex max-w-full flex-wrap gap-1">
            {chatbot?.suggested_messages?.map((message, idx) => (
              <Pill
                style={{
                  cursor: 'pointer',
                  backgroundColor: chatbot?.ai_background_color || 'gray',
                  color: chatbot?.ai_text_color || 'white',
                  fontSize: '12px',
                }}
                onClick={() => {
                  setShowSuggestedMessages(false)
                  setMessages([
                    ...messages,
                    { role: 'assistant', content: message },
                  ])
                  vectorSearch(message)
                }}
                key={idx}
                hasMaxWidth={false}
                color="gray"
              >
                {message}
              </Pill>
            ))}
          </div>
        )}
      </div>
      {showClearConversation && (
        <button
          className="mb-2 mt-4 w-full text-right text-xs text-gray-400 hover:text-gray-900"
          onClick={handleClearMessages}
        >
          Clear Conversation
        </button>
      )}
      <div ref={endOfMessagesRef} />
    </ul>
  )
}
